import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useNotificationSSE } from '@/hooks/useNotificationSSE';
import {
  deleteNotificationApi,
  getNotificationCountApi,
  getNotificationsApi,
  markAllNotificationsReadApi,
  markNotificationReadApi,
} from '@/lib/api/notificationsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { NotificationType } from '@shared/enums/domain.enum';
import type { AuthUser, Notification } from '@shared/types/entities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Bell,
  Building2,
  CheckCircle2,
  CreditCard,
  RefreshCw,
  ShieldCheck,
  ShieldX,
  Trophy,
  UserCheck,
  UserPlus,
  UserX,
  Users,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatRelativeDate(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "À l'instant";
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `il y a ${days} j`;
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function getNotificationIcon(type: NotificationType): React.ReactElement {
  const cls = 'h-4 w-4 shrink-0';
  switch (type) {
    case NotificationType.TEAM_REGISTERED:
      return <Users className={cls} />;
    case NotificationType.TEAM_CONFIRMED:
      return <CheckCircle2 className={`${cls} text-green-500`} />;
    case NotificationType.TEAM_REJECTED:
      return <XCircle className={`${cls} text-red-500`} />;
    case NotificationType.PAYMENT_CONFIRMED:
      return <CreditCard className={`${cls} text-blue-500`} />;
    case NotificationType.PAYMENT_REFUNDED:
    case NotificationType.PAYMENT_PARTIALLY_REFUNDED:
      return <RefreshCw className={`${cls} text-amber-500`} />;
    case NotificationType.COMPETITION_RESULTS:
      return <Trophy className={`${cls} text-yellow-500`} />;
    case NotificationType.MEMBER_REQUEST:
      return <UserPlus className={cls} />;
    case NotificationType.MEMBER_APPROVED:
      return <UserCheck className={`${cls} text-green-500`} />;
    case NotificationType.MEMBER_REJECTED:
      return <UserX className={`${cls} text-red-500`} />;
    case NotificationType.AFFILIATION_REQUEST:
    case NotificationType.AFFILIATION_APPROVED:
      return <Building2 className={cls} />;
    case NotificationType.REFUND_REQUEST_CREATED:
      return <RefreshCw className={`${cls} text-amber-500`} />;
    case NotificationType.REFUND_APPROVED:
      return <CheckCircle2 className={`${cls} text-green-500`} />;
    case NotificationType.REFUND_REJECTED:
      return <XCircle className={`${cls} text-red-500`} />;
    case NotificationType.CLAIM_APPROVED:
      return <ShieldCheck className={`${cls} text-green-500`} />;
    case NotificationType.CLAIM_REJECTED:
      return <ShieldX className={`${cls} text-red-500`} />;
    case NotificationType.SUBSCRIPTION_PAYMENT_FAILED:
      return <AlertTriangle className={`${cls} text-red-500`} />;
    case NotificationType.SUBSCRIPTION_CANCELLED:
      return <XCircle className={`${cls} text-slate-500`} />;
    case NotificationType.PAYMENT_DISPUTED:
      return <AlertTriangle className={`${cls} text-red-600`} />;
    case NotificationType.COMPETITION_REMINDER_J7:
    case NotificationType.COMPETITION_REMINDER_J1:
      return <Bell className={`${cls} text-blue-400`} />;
    default:
      return <Bell className={cls} />;
  }
}

const ADMIN_URL = (import.meta.env.VITE_URL_ADMIN as string | undefined)?.replace(/\/$/, '') ?? 'http://localhost:5231';

function getNotificationRoute(notif: Notification, user: AuthUser | null): string {
  const organizationId = user?.organizationsAdmin[0]?.organizationId;
  switch (notif.type) {
    case NotificationType.TEAM_REGISTERED:
    case NotificationType.TEAM_CONFIRMED:
    case NotificationType.TEAM_REJECTED:
    case NotificationType.PAYMENT_CONFIRMED:
    case NotificationType.PAYMENT_REFUNDED:
    case NotificationType.PAYMENT_PARTIALLY_REFUNDED:
    case NotificationType.REFUND_REQUEST_CREATED:
    case NotificationType.REFUND_APPROVED:
    case NotificationType.REFUND_REJECTED:
      return notif.competitionId && organizationId
        ? `${ADMIN_URL}/organizer/inscriptions?competitionId=${notif.competitionId}`
        : `${ADMIN_URL}/player/inscriptions`;
    case NotificationType.COMPETITION_RESULTS:
      return notif.competitionId ? `/concours/${notif.competitionId}` : '/concours';
    case NotificationType.MEMBER_REQUEST:
    case NotificationType.MEMBER_APPROVED:
      return '/mon-club';
    case NotificationType.MEMBER_REJECTED:
      return '/';
    case NotificationType.AFFILIATION_REQUEST:
      return '/';
    case NotificationType.AFFILIATION_APPROVED:
      return '/mon-club';
    case NotificationType.CLAIM_APPROVED:
    case NotificationType.CLAIM_REJECTED:
      return '/mon-club';
    case NotificationType.SUBSCRIPTION_PAYMENT_FAILED:
    case NotificationType.SUBSCRIPTION_CANCELLED:
    case NotificationType.PAYMENT_DISPUTED:
      return '/mon-espace';
    case NotificationType.COMPETITION_REMINDER_J7:
    case NotificationType.COMPETITION_REMINDER_J1:
      return notif.competitionId ? `/concours/${notif.competitionId}` : '/mon-espace';
    default:
      return '/mon-espace';
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function NotificationBell() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const user = useAuthStore(s => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { fallbackPolling } = useNotificationSSE();

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | undefined>(undefined);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // ─── Query: unread count (poll every 30s) ────────────────────────────────
  const { data: countData } = useQuery({
    queryKey: queryKeys.notifications.count(),
    queryFn: getNotificationCountApi,
    enabled: isAuthenticated,
    refetchInterval: fallbackPolling ? 30_000 : false,
    refetchOnWindowFocus: true,
  });

  const unreadCount = countData?.unread ?? 0;

  // ─── Query: notifications list (lazy — only when dropdown open) ──────────
  const { data: listData, isLoading: listLoading } = useQuery({
    queryKey: queryKeys.notifications.list(),
    queryFn: () => getNotificationsApi({ page: 1, limit: 20 }),
    enabled: isAuthenticated && open,
  });

  const notifications = listData?.data ?? [];

  // ─── Mutations ───────────────────────────────────────────────────────────
  const markReadMutation = useMutation({
    mutationFn: markNotificationReadApi,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list() });
    },
    onError: () => {
      toast.error('Impossible de marquer la notification comme lue.');
    },
  });

  const markAllReadMutation = useMutation({
    mutationFn: markAllNotificationsReadApi,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list() });
      toast.success('Toutes les notifications ont été marquées comme lues.');
    },
    onError: () => {
      toast.error('Une erreur est survenue.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteNotificationApi,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.count() });
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list() });
    },
    onError: () => {
      toast.error('Impossible de supprimer la notification.');
    },
  });

  // ─── Handlers ────────────────────────────────────────────────────────────
  function handleNotificationClick(notif: Notification, user: AuthUser | null) {
    if (!notif.isRead) {
      markReadMutation.mutate(notif.id);
    }
    setOpen(false);
    const route = getNotificationRoute(notif, user);
    if (route.startsWith('http')) {
      globalThis.location.assign(route);
    } else {
      navigate(route);
    }
  }

  function handleMarkAllRead(e: React.MouseEvent) {
    e.stopPropagation();
    markAllReadMutation.mutate();
  }

  function handleDelete(e: React.MouseEvent, id: number) {
    e.stopPropagation();
    deleteMutation.mutate(id);
  }

  if (!isAuthenticated) return null;

  return (
    <div ref={containerRef as React.RefObject<HTMLDivElement>} className='relative'>
      {/* Bell button */}
      <button
        type='button'
        onClick={() => setOpen(prev => !prev)}
        className='relative flex h-9 w-9 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-700 cursor-pointer'
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} non lues)` : ''}`}
      >
        <Bell className='h-5 w-5' />
        {unreadCount > 0 && (
          <span className='absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-0.5 text-[10px] font-bold leading-none text-white'>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className='absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800'>
          {/* Header */}
          <div className='flex items-center justify-between border-b border-slate-100 px-4 py-3 dark:border-slate-700'>
            <div className='flex items-center gap-2'>
              <span className='text-sm font-semibold text-slate-800 dark:text-slate-100'>Notifications</span>
              {unreadCount > 0 && (
                <span className='rounded-full bg-red-100 px-2 py-0.5 text-xs font-bold text-red-600 dark:bg-red-900/40 dark:text-red-400'>
                  {unreadCount}
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                type='button'
                onClick={handleMarkAllRead}
                disabled={markAllReadMutation.isPending}
                className='text-xs font-medium text-blue-600 transition hover:text-blue-800 disabled:opacity-50 dark:text-blue-400 dark:hover:text-blue-300'
              >
                Tout marquer lu
              </button>
            )}
          </div>

          {/* List */}
          <div className='max-h-90 overflow-y-auto'>
            {listLoading ? (
              <div className='flex flex-col gap-2 p-4'>
                {[1, 2, 3].map(i => (
                  <div key={i} className='h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-700' />
                ))}
              </div>
            ) : notifications.length === 0 ? (
              <div className='flex flex-col items-center gap-2 py-10 text-center'>
                <Bell className='h-8 w-8 text-slate-300 dark:text-slate-600' />
                <p className='text-sm text-slate-500 dark:text-slate-400'>Aucune notification</p>
              </div>
            ) : (
              <ul>
                {notifications.map(notif => (
                  <li key={notif.id}>
                    <a
                      type='button'
                      onClick={() => handleNotificationClick(notif, user)}
                      className={`group flex w-full items-start gap-3 px-4 py-3 text-left transition hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer ${
                        !notif.isRead ? 'bg-blue-50/60 dark:bg-blue-900/10' : ''
                      }`}
                    >
                      <div className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'>
                        {getNotificationIcon(notif.type)}
                      </div>
                      <div className='min-w-0 flex-1'>
                        <p
                          className={`text-sm leading-snug ${!notif.isRead ? 'font-semibold text-slate-800 dark:text-slate-100' : 'font-medium text-slate-700 dark:text-slate-200'}`}
                        >
                          {notif.title}
                        </p>
                        <p className='mt-0.5 line-clamp-2 text-xs text-slate-500 dark:text-slate-400'>{notif.body}</p>
                        <p className='mt-1 text-xs text-slate-400 dark:text-slate-500'>
                          {formatRelativeDate(notif.createdAt)}
                        </p>
                      </div>
                      {!notif.isRead && <span className='mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500' />}
                      <button
                        type='button'
                        onClick={e => handleDelete(e, notif.id)}
                        className='cursor-pointer ml-1 mt-0.5 hidden shrink-0 rounded p-0.5 text-slate-400 transition hover:text-red-500 group-hover:flex dark:hover:bg-slate-600 dark:hover:text-slate-200'
                        aria-label='Supprimer la notification'
                        title='Supprimer'
                      >
                        <XCircle className='h-3.5 w-3.5' />
                      </button>
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className='border-t border-slate-100 px-4 py-2 dark:border-slate-700'>
              <p className='text-center text-xs text-slate-400 dark:text-slate-500'>
                {listData?.meta.total && listData.meta.total > notifications.length
                  ? `${notifications.length} sur ${listData.meta.total} notifications`
                  : `${notifications.length} notification${notifications.length > 1 ? 's' : ''}`}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
